import { Mail, Phone, MapPin, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import BookingButton from "@/components/BookingButton";
import ContactInfoCard from "@/components/ContactInfoCard";
import { COMPANY, REGIONS, CONTACT_INFO, telHref } from "@/data/content";

export default function Contact() {
  return (
    <>
      <SEO title="Contact" description={`Contact DataMeridian Consulting. Email ${CONTACT_INFO.email} or call ${CONTACT_INFO.phonePrimary} to talk to a senior database consultant.`} />

      <section className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container py-24 lg:py-32">
          <div className="dm-overline text-[var(--dm-emerald)]">Contact</div>
          <h1 className="font-serif text-5xl sm:text-6xl mt-6 max-w-4xl leading-[1.05]">Talk to a senior database specialist.</h1>
          <p className="text-white/70 text-lg mt-8 max-w-2xl">Reach out about a specific project, request a database health assessment, or ask a technical question. Every message is read by a senior consultant.</p>
        </div>
      </section>

      {/* Prominent quick-contact strip */}
      <section data-testid="quick-contact-strip" className="bg-white border-b border-[var(--dm-border)]">
        <div className="dm-container grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[var(--dm-border)]">
          <a href={`mailto:${CONTACT_INFO.email}`} data-testid="quick-contact-email"
             className="flex items-center gap-4 py-6 md:py-8 md:px-8 hover:bg-[var(--dm-surface)] transition-colors">
            <Mail className="w-5 h-5 text-[var(--dm-emerald)]" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--dm-muted)]">Email</div>
              <div className="font-serif text-lg text-[var(--dm-navy)] break-all">{CONTACT_INFO.email}</div>
            </div>
          </a>
          <a href={telHref(CONTACT_INFO.phonePrimary)} data-testid="quick-contact-phone-primary"
             className="flex items-center gap-4 py-6 md:py-8 md:px-8 hover:bg-[var(--dm-surface)] transition-colors">
            <Phone className="w-5 h-5 text-[var(--dm-emerald)]" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--dm-muted)]">Phone</div>
              <div className="font-serif text-lg text-[var(--dm-navy)]">{CONTACT_INFO.phonePrimary}</div>
            </div>
          </a>
          <a href={telHref(CONTACT_INFO.phoneAlternate)} data-testid="quick-contact-phone-alt"
             className="flex items-center gap-4 py-6 md:py-8 md:px-8 hover:bg-[var(--dm-surface)] transition-colors">
            <Phone className="w-5 h-5 text-[var(--dm-emerald)]" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--dm-muted)]">Alternate</div>
              <div className="font-serif text-lg text-[var(--dm-navy)]">{CONTACT_INFO.phoneAlternate}</div>
            </div>
          </a>
        </div>
      </section>

      <section className="bg-white">
        <div className="dm-container dm-section grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 space-y-10">
            <ContactInfoCard variant="light" testId="contact-info-card-desktop" />

            <div className="flex flex-col sm:flex-row gap-3">
              <BookingButton label="Request Technical Assessment" meetingType="consultation" variant="primary" testId="contact-request-assessment" />
              <BookingButton label="Schedule Discovery Call" meetingType="discovery" variant="outline" testId="contact-book-discovery" />
            </div>

            <div>
              <div className="dm-overline">Regions we serve</div>
              <div className="grid grid-cols-2 gap-2 mt-6">
                {REGIONS.map((r) => (
                  <div key={r} className="flex items-center gap-2 text-sm text-[var(--dm-navy)]">
                    <MapPin className="w-3.5 h-3.5 text-[var(--dm-emerald)]" /> {r}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-[var(--dm-surface)] p-8 lg:p-10 border border-[var(--dm-border)]">
              <div className="dm-overline">Talk to a Specialist</div>
              <h2 className="font-serif text-3xl mt-4 text-[var(--dm-navy)] mb-8">Tell us about your database environment.</h2>
              <ContactForm sourcePage="contact" />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Contact Section — bottom sticky-style block */}
      <section data-testid="mobile-contact-section" className="bg-[var(--dm-surface)] border-t border-[var(--dm-border)] lg:hidden">
        <div className="dm-container py-10">
          <div className="dm-overline">Mobile Contact</div>
          <h3 className="font-serif text-2xl text-[var(--dm-navy)] mt-3">Prefer to talk now?</h3>
          <div className="mt-6 grid gap-3">
            <a href={telHref(CONTACT_INFO.phonePrimary)} data-testid="mobile-call-primary" className="dm-btn-primary justify-between">
              <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call {CONTACT_INFO.phonePrimary}</span>
            </a>
            <a href={telHref(CONTACT_INFO.phoneAlternate)} data-testid="mobile-call-alt" className="dm-btn-outline justify-between">
              <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call {CONTACT_INFO.phoneAlternate}</span>
            </a>
            <a href={`mailto:${CONTACT_INFO.email}`} data-testid="mobile-email" className="dm-btn-outline justify-between">
              <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4" /> {CONTACT_INFO.email}</span>
            </a>
          </div>
          <div className="mt-6 flex items-center gap-2 text-xs text-[var(--dm-muted)]">
            <Clock className="w-3.5 h-3.5 text-[var(--dm-emerald)]" />
            {CONTACT_INFO.availabilityDays} · {CONTACT_INFO.availabilityHours} · {CONTACT_INFO.availabilityNote}
          </div>
          <div className="mt-6 text-xs text-[var(--dm-muted)] uppercase tracking-[0.2em]">{COMPANY.tagline}</div>
        </div>
      </section>
    </>
  );
}
